/**
 * Beehiiv API helpers for newsletter posts and subscriptions.
 * Requests go through the same-origin proxy (/beehiiv-api) in dev to avoid CORS;
 * the Vite server injects the API key. For production, use VITE_BEEHIIV_API_PROXY_URL.
 * @see https://developers.beehiiv.com/api-reference/posts/index
 * @see https://developers.beehiiv.com/api-reference/subscriptions/create
 */

const BEEHIIV_BASE =
  import.meta.env.VITE_BEEHIIV_API_PROXY_URL ?? "/beehiiv-api";

function getConfig() {
  const publicationId = import.meta.env.VITE_BEEHIIV_PUBLICATION_ID?.trim();
  return { publicationId };
}

function requestHeaders(): HeadersInit {
  return { "Content-Type": "application/json" };
}

export interface BeehiivPost {
  id: string;
  title: string;
  subtitle: string | null;
  slug: string;
  authors: string[];
  created: number;
  publish_date: number;
  displayed_date?: number;
  thumbnail_url: string;
  web_url: string;
  subject_line: string;
  preview_text: string | null;
  content_tags: string[];
  content?: {
    free?: {
      web?: string;
      email?: string;
      rss?: string;
    };
    premium?: {
      web?: string;
      email?: string;
    };
  };
}

export interface BeehiivPostsListResponse {
  data: BeehiivPost[];
  limit: number;
  page: number;
  total_results: number;
  total_pages: number;
}

export interface BeehiivSubscriptionRequest {
  email: string;
  reactivate_existing?: boolean;
  send_welcome_email?: boolean;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referring_site?: string;
  custom_fields?: { name: string; value: string | number | boolean }[];
}

export type BeehiivPostsFetchOutcome =
  | { ok: true; data: BeehiivPostsListResponse }
  | { ok: false; status: number; message: string };

async function parseBeehiivError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as {
      message?: string;
      errors?: Array<{ message?: string }>;
    };
    const first = j.errors?.[0]?.message;
    return (first ?? j.message ?? text).slice(0, 400);
  } catch {
    return text.slice(0, 400) || res.statusText;
  }
}

/**
 * Same as fetchPosts but returns HTTP/API error details (useful for debugging in UI).
 */
export async function fetchPostsDetailed(options: {
  page?: number;
  limit?: number;
  order_by?: "created" | "publish_date" | "displayed_date";
  direction?: "asc" | "desc";
  status?: "draft" | "confirmed" | "archived" | "all";
  content_tags?: string[];
  hidden_from_feed?: "all" | "true" | "false";
  platform?: "web" | "email" | "both" | "all";
}): Promise<BeehiivPostsFetchOutcome> {
  const { publicationId } = getConfig();
  if (!publicationId) {
    return {
      ok: false,
      status: 0,
      message: "Missing VITE_BEEHIIV_PUBLICATION_ID",
    };
  }

  const params = new URLSearchParams();
  params.set("limit", String(options.limit ?? 10));
  params.set("page", String(options.page ?? 1));
  params.set("order_by", options.order_by ?? "publish_date");
  params.set("direction", options.direction ?? "desc");
  params.set("status", options.status ?? "confirmed");
  if (options.hidden_from_feed) {
    params.set("hidden_from_feed", options.hidden_from_feed);
  }
  if (options.platform) {
    params.set("platform", options.platform);
  }
  options.content_tags?.forEach((tag) => params.append("content_tags[]", tag));

  const url = `${BEEHIIV_BASE}/publications/${publicationId}/posts?${params}`;
  let res: Response;
  try {
    res = await fetch(url, { headers: requestHeaders() });
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Network error";
    return {
      ok: false,
      status: 0,
      message: `${msg}. If using \`vite preview\`, the dev proxy is off—use \`npm run dev\` or a deployed /beehiiv-api proxy.`,
    };
  }

  if (!res.ok) {
    const message = await parseBeehiivError(res);
    return { ok: false, status: res.status, message };
  }

  const data = (await res.json()) as BeehiivPostsListResponse;
  return { ok: true, data };
}

/** List posts with pagination. Use status=archived for published-only if needed; API may default to visible. */
export async function fetchPosts(options: {
  page?: number;
  limit?: number;
  order_by?: "created" | "publish_date" | "displayed_date";
  direction?: "asc" | "desc";
  status?: "draft" | "confirmed" | "archived" | "all";
  content_tags?: string[];
  /** Feed visibility filter (Beehiiv: false = only posts visible on site feed). */
  hidden_from_feed?: "all" | "true" | "false";
  platform?: "web" | "email" | "both" | "all";
}): Promise<BeehiivPostsListResponse | null> {
  const out = await fetchPostsDetailed(options);
  return out.ok ? out.data : null;
}

/** Fetch a single post by slug (for /newsletter/:slug). Expands free_web_content for body. */
export async function fetchPostBySlug(slug: string): Promise<BeehiivPost | null> {
  const { publicationId } = getConfig();
  if (!publicationId) return null;

  const params = new URLSearchParams();
  params.set("slugs[]", slug);
  params.set("limit", "1");
  params.set("expand", "free_web_content");

  const url = `${BEEHIIV_BASE}/publications/${publicationId}/posts?${params}`;
  const res = await fetch(url, { headers: requestHeaders() });
  if (!res.ok) return null;
  const json: BeehiivPostsListResponse = await res.json();
  return json.data?.[0] ?? null;
}

/** Create a Beehiiv subscription (add subscriber to newsletter). */
export async function createSubscription(
  body: BeehiivSubscriptionRequest
): Promise<{ ok: boolean; error?: string }> {
  const { publicationId } = getConfig();
  if (!publicationId) {
    return { ok: false, error: "Beehiiv not configured" };
  }

  const url = `${BEEHIIV_BASE}/publications/${publicationId}/subscriptions`;
  const res = await fetch(url, {
    method: "POST",
    headers: requestHeaders(),
    body: JSON.stringify({
      email: body.email,
      reactivate_existing: body.reactivate_existing ?? false,
      send_welcome_email: body.send_welcome_email ?? false,
      utm_source: body.utm_source,
      utm_medium: body.utm_medium,
      utm_campaign: body.utm_campaign,
      utm_term: body.utm_term,
      utm_content: body.utm_content,
      referring_site: body.referring_site,
      custom_fields: body.custom_fields,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    return { ok: false, error: (err as { message?: string })?.message || res.statusText };
  }
  return { ok: true };
}
